'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/lib/i18n/language-provider'
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Sparkles,
  CheckCircle2,
  ArrowRight
} from 'lucide-react'
import type { OnboardingStep, OnboardingTargetPosition } from '@/types/onboarding'

interface OnboardingTooltipProps {
  step: OnboardingStep
  onNext: () => void
  onPrev: () => void
  onSkip: () => void
  currentIndex: number
  totalSteps: number
  isFirst: boolean
  isLast: boolean
  cameFromBack?: boolean
  isDragging?: boolean
}

interface Position {
  top: number
  left: number
  arrowPosition: 'top' | 'bottom' | 'left' | 'right'
}

const TOOLTIP_WIDTH = 340
const TOOLTIP_MARGIN = 16
const ARROW_SIZE = 10

export function OnboardingTooltip({
  step,
  onNext,
  onPrev,
  onSkip,
  currentIndex,
  totalSteps,
  isFirst,
  isLast,
  cameFromBack = false,
  isDragging = false,
}: OnboardingTooltipProps) {
  const { t } = useLanguage()
  const [position, setPosition] = useState<Position>({ top: 0, left: 0, arrowPosition: 'top' })
  const [isVisible, setIsVisible] = useState(false)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const calculatePosition = useCallback(() => {
    // Fixed right positioning: place tooltip on the right side of the viewport (checked first, independent of target)
    if (step.tooltipFixedRight || isDragging) {
      const tooltipHeight = tooltipRef.current?.offsetHeight || 180
      setPosition({
        top: window.innerHeight / 2 - tooltipHeight / 2,
        left: window.innerWidth - TOOLTIP_WIDTH - TOOLTIP_MARGIN * 2,
        arrowPosition: 'left',
      })
      return
    }

    if (!step.target) {
      // Center on screen if no target
      setPosition({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - TOOLTIP_WIDTH / 2,
        arrowPosition: 'top',
      })
      return
    }

    const targetElement = document.querySelector(step.target)
    if (!targetElement) {
      // Fallback to center
      setPosition({
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - TOOLTIP_WIDTH / 2,
        arrowPosition: 'top',
      })
      return
    }

    const targetRect = targetElement.getBoundingClientRect()
    const tooltipHeight = tooltipRef.current?.offsetHeight || 180
    const padding = step.spotlightPadding || 8

    let top = 0
    let left = 0
    let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'top'

    const tooltipPosition = step.tooltipPosition || 'bottom'

    switch (tooltipPosition) {
      case 'top':
        top = targetRect.top - tooltipHeight - TOOLTIP_MARGIN - padding
        left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2
        arrowPosition = 'bottom'
        break
      case 'bottom':
        top = targetRect.bottom + TOOLTIP_MARGIN + padding
        left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2
        arrowPosition = 'top'
        break
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2
        left = targetRect.left - TOOLTIP_WIDTH - TOOLTIP_MARGIN - padding
        arrowPosition = 'right'
        break
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2
        left = targetRect.right + TOOLTIP_MARGIN + padding
        arrowPosition = 'left'
        break
      default:
        top = targetRect.bottom + TOOLTIP_MARGIN + padding
        left = targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2
        arrowPosition = 'top'
    }

    // Ensure tooltip stays within viewport
    if (left < TOOLTIP_MARGIN) left = TOOLTIP_MARGIN
    if (left + TOOLTIP_WIDTH > window.innerWidth - TOOLTIP_MARGIN) {
      left = window.innerWidth - TOOLTIP_WIDTH - TOOLTIP_MARGIN
    }
    if (top < TOOLTIP_MARGIN) top = TOOLTIP_MARGIN
    if (top + tooltipHeight > window.innerHeight - TOOLTIP_MARGIN) {
      top = window.innerHeight - tooltipHeight - TOOLTIP_MARGIN
    }

    setPosition({ top, left, arrowPosition })
  }, [step, isDragging])

  useEffect(() => {
    // Small delay for animation
    const showTimer = setTimeout(() => {
      setIsVisible(true)
    }, step.delay || 100)

    calculatePosition()

    const handleResize = () => {
      requestAnimationFrame(calculatePosition)
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize, true)

    return () => {
      clearTimeout(showTimer)
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize, true)
    }
  }, [calculatePosition, step.delay])

  // Recalculate when tooltip renders
  useEffect(() => {
    if (tooltipRef.current) {
      calculatePosition()
    }
  }, [calculatePosition])

  const getArrowStyles = () => {
    const base = "absolute w-0 h-0 border-solid"
    switch (position.arrowPosition) {
      case 'top':
        return cn(base, "top-0 left-1/2 -translate-x-1/2 -translate-y-full border-l-[10px] border-r-[10px] border-b-[10px] border-l-transparent border-r-transparent border-b-background")
      case 'bottom':
        return cn(base, "bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent border-t-background")
      case 'left':
        return cn(base, "left-0 top-1/2 -translate-x-full -translate-y-1/2 border-t-[10px] border-b-[10px] border-r-[10px] border-t-transparent border-b-transparent border-r-background")
      case 'right':
        return cn(base, "right-0 top-1/2 translate-x-full -translate-y-1/2 border-t-[10px] border-b-[10px] border-l-[10px] border-t-transparent border-b-transparent border-l-background")
      default:
        return base
    }
  }

  const progressPercent = ((currentIndex + 1) / totalSteps) * 100

  return (
    <div
      ref={tooltipRef}
      className={cn(
        "fixed z-[9999] transition-all duration-300 ease-out",
        isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
      )}
      style={{
        top: position.top,
        left: position.left,
        width: TOOLTIP_WIDTH,
      }}
    >
      {/* Arrow */}
      {step.target && <div className={getArrowStyles()} />}

      {/* Tooltip content */}
      <div className="bg-background border border-border rounded-xl shadow-2xl overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-muted">
          <div 
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Header */}
        <div className="p-4 pb-2">
          <div className="flex items-start justify-between gap-2 mb-2">
            <Badge variant="secondary" className="bg-primary/10 text-primary text-xs">
              <Sparkles className="h-3 w-3 mr-1" />
              {t('onboarding.step', { current: currentIndex + 1, total: totalSteps }) || `Étape ${currentIndex + 1}/${totalSteps}`}
            </Badge>
            {step.showSkip && (
              <button
                onClick={onSkip}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
                aria-label={t('onboarding.aria.skipTutorial')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <h3 className="text-lg font-semibold text-foreground leading-tight">
            {step.titleKey ? t(step.titleKey) : step.title}
          </h3>
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {step.descriptionKey ? t(step.descriptionKey) : step.description}
          </p>

          {/* Action hint */}
          {step.requiredAction && step.allowInteraction && (
            <div className="mt-3 p-2 bg-primary/5 rounded-lg border border-primary/20">
              <p className="text-xs text-primary flex items-center gap-1.5">
                <ArrowRight className="h-3 w-3" />
                {getActionHint(step.requiredAction.type, t)}
              </p>
            </div>
          )}
        </div>

        {/* Footer with navigation */}
        <div className="px-4 pb-4 flex items-center justify-between gap-2">
          <div className="flex gap-2">
            {step.showBack && !isFirst && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onPrev}
                className="text-muted-foreground"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                {t('onboarding.button.back')}
              </Button>
            )}
          </div>

          {/* Only show Continue button if no action is required OR if not waiting for action OR if came from back navigation */}
          {(!step.nextOnAction || !step.requiredAction || cameFromBack) && (
            <Button
              size="sm"
              onClick={onNext}
              className={cn(
                "min-w-[100px]",
                isLast && "bg-green-600 hover:bg-green-700"
              )}
            >
              {isLast ? (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  {t('onboarding.button.finish')}
                </>
              ) : (
                <>
                  {t('onboarding.button.next')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function getActionHint(actionType: string, t: (key: string) => string): string {
  const hintMap: Record<string, string> = {
    'click': 'onboarding.hint.click',
    'drag': 'onboarding.hint.drag',
    'select': 'onboarding.hint.select',
    'input': 'onboarding.hint.input',
    'step-change': 'onboarding.hint.stepChange',
    'processor-placed': 'onboarding.hint.processorPlaced',
    'processor-selected': 'onboarding.hint.processorSelected',
    'processor-dragging': 'onboarding.hint.processorDragging',
    'cabinet-placed': 'onboarding.hint.cabinetPlaced',
    'cabinet-selected': 'onboarding.hint.cabinetSelected',
    'cabinet-dragging': 'onboarding.hint.cabinetDragging',
    'port-selected': 'onboarding.hint.portSelected',
    'port-dragging': 'onboarding.hint.portDragging',
    'outlet-selected': 'onboarding.hint.outletSelected',
    'panels-cabled': 'onboarding.hint.panelsCabled',
    'redundancy-created': 'onboarding.hint.redundancyCreated',
    'mire-customized': 'onboarding.hint.mireCustomized',
    'mire-added-to-list': 'onboarding.hint.mireAddedToList',
    'mire-used': 'onboarding.hint.mireUsed',
    'cartouche-filled': 'onboarding.hint.cartoucheFilled',
    'png-exported': 'onboarding.hint.pngExported',
  }
  const key = hintMap[actionType]
  return key ? t(key) : t('onboarding.hint.default')
}
